import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useStaffPayoutsHook } from '../hooks/useStaffPayoutsHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import {
  IStaffPayout,
  IStaffPayoutResponse,
} from '../../../../apis/staff/staffPayout.interfaces';

type TStaffPayoutAppState = {
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  isModalOpen: boolean;
  selectedPayoutForEdit: IStaffPayout | null;
  mutationErrorMessage?: string;
  isMutationLoading: boolean;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MODAL_OPEN = 'SET_IS_MODAL_OPEN',
  SET_SELECTED_PAYOUT_FOR_EDIT = 'SET_SELECTED_PAYOUT_FOR_EDIT',
  SET_MUTATION_ERROR_MESSAGE = 'SET_MUTATION_ERROR_MESSAGE',
  SET_MUTATION_LOADING = 'SET_MUTATION_LOADING',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_IS_MODAL_OPEN; payload: boolean }
  | {
      type: ACTION_TYPE.SET_SELECTED_PAYOUT_FOR_EDIT;
      payload: IStaffPayout | null;
    }
  | {
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE;
      payload: string | undefined;
    }
  | { type: ACTION_TYPE.SET_MUTATION_LOADING; payload: boolean };

const initialStaffPayoutState: TStaffPayoutAppState = {
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  isModalOpen: false,
  selectedPayoutForEdit: null,
  mutationErrorMessage: undefined,
  isMutationLoading: false,
};

type TState = {
  staffPayoutAppState: TStaffPayoutAppState;
  staffPayoutRecords: IStaffPayoutResponse;
};

const StaffPayoutContext = createContext<TState | undefined>(undefined);
const StaffPayoutDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const staffPayoutReducer = (
  appState: TStaffPayoutAppState,
  action: Action
): TStaffPayoutAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MODAL_OPEN:
      return { ...appState, isModalOpen: action.payload };
    case ACTION_TYPE.SET_SELECTED_PAYOUT_FOR_EDIT:
      return { ...appState, selectedPayoutForEdit: action.payload };
    case ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE:
      return { ...appState, mutationErrorMessage: action.payload };
    case ACTION_TYPE.SET_MUTATION_LOADING:
      return { ...appState, isMutationLoading: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StaffPayoutProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [staffPayoutAppState, dispatch] = useReducer(
    staffPayoutReducer,
    initialStaffPayoutState
  );

  const {
    data: staffPayoutResponse,
    isLoading: staffPayoutResponseLoading,
    isError: staffPayoutResponseLoadingError,
    isSuccess: staffPayoutResponseLoadingDataSuccess,
    error: staffPayoutResponseDataError,
  } = useStaffPayoutsHook(staffPayoutAppState.userParams);

  useEffect(() => {
    onStateChangeHandler();
    // eslint-disable-next-line
  }, [
    staffPayoutResponseDataError,
    staffPayoutResponseLoading,
    staffPayoutResponseLoadingError,
    staffPayoutResponseLoadingDataSuccess,
  ]);

  const onStateChangeHandler = () => {
    if (staffPayoutResponseLoading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (staffPayoutResponseLoadingDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (staffPayoutResponseLoadingError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          staffPayoutResponseDataError?.response?.data?.message ||
          ERROR_MESSAGE.GET_STAFF_PAYOUT_FAILURE,
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      staffPayoutAppState,
      staffPayoutRecords: staffPayoutResponse ?? {
        data: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      },
    }),
    [staffPayoutAppState, staffPayoutResponse]
  );

  return (
    <StaffPayoutDispatchContext.Provider value={dispatch}>
      <StaffPayoutContext.Provider value={currentState}>
        {children}
      </StaffPayoutContext.Provider>
    </StaffPayoutDispatchContext.Provider>
  );
};

export const useStaffPayoutState = () => {
  const state = useContext(StaffPayoutContext);
  if (state === undefined) {
    throw new Error(
      'useStaffPayoutState must be used within a StaffPayoutProvider'
    );
  }
  return state;
};

export const useStaffPayoutActions = () => {
  const dispatch = useContext(StaffPayoutDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStaffPayoutActions dispatch must be within a StaffPayoutProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const openModal = (payout?: IStaffPayout) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_PAYOUT_FOR_EDIT,
      payload: payout || null,
    });
    dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: true });
    dispatch({
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
      payload: undefined,
    });
  };

  const closeModal = () => {
    dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: false });
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_PAYOUT_FOR_EDIT,
      payload: null,
    });
    dispatch({
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
      payload: undefined,
    });
  };

  const setSelectedPayoutForEdit = (payout?: IStaffPayout) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_PAYOUT_FOR_EDIT,
      payload: payout || null,
    });
  };

  return {
    updateSearchParams,
    openModal,
    closeModal,
    setSelectedPayoutForEdit,
  };
};
